import { memo, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import type { DataTableBulkAction } from './data-table.interface'

interface DataTableBulkActionsProps {
  selectedCount: number
  bulkActionList: DataTableBulkAction[]
  onBulkAction?: (actionId: string) => void
}

/**
 * Renders bulk action controls when rows are selected.
 * Shows selection count and a dropdown with available bulk actions.
 */
const DataTableBulkActionsBase: React.FC<DataTableBulkActionsProps> = ({
  selectedCount,
  bulkActionList,
  onBulkAction,
}): ReactNode => (
  <div className="data-table-bulk-actions flex items-center gap-2">
    <span className="text-sm text-muted-foreground">
      {selectedCount} row{selectedCount !== 1 ? 's' : ''} selected
    </span>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8">
          Actions
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {bulkActionList.map((action) => (
          <DropdownMenuItem
            key={action.id}
            onClick={() => onBulkAction?.(action.id)}
            variant={action.variant}
          >
            {action.icon && <span className="mr-2">{action.icon}</span>}
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
)

export const DataTableBulkActions = memo(DataTableBulkActionsBase)

DataTableBulkActions.displayName = 'DataTableBulkActions'
